/* eslint-disable react/no-unknown-property */
/**
 * Static SVG ornaments — sacred geometry, temple motifs, manuscript flourishes.
 * All elements are server-cheap inline SVGs; no animation, no JS.
 * Color follows `currentColor` so they can be tinted via Tailwind text-* utilities.
 */

export function OmMark({ size = 48, className = "", strokeWidth = 1.2 }) {
  // Stylised Om — built from geometry rather than the Unicode glyph for crisp rendering.
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <circle cx="32" cy="32" r="30" stroke="currentColor" strokeWidth="0.6" opacity="0.35" />
      <text
        x="32"
        y="44"
        textAnchor="middle"
        fontFamily="'Cormorant Garamond', serif"
        fontSize="42"
        fontWeight="500"
        fill="currentColor"
      >
        ॐ
      </text>
    </svg>
  );
}

/** Horizontal divider used above section titles. Two thin rules + central lotus seed. */
export function DecorativeDivider({ label, className = "" }) {
  return (
    <div
      className={`flex items-center justify-center gap-3 text-[color:var(--brand)] ${className}`}
      aria-hidden="true"
    >
      <svg width="80" height="16" viewBox="0 0 80 16">
        <line x1="0" y1="8" x2="30" y2="8" stroke="currentColor" strokeWidth="0.8" />
        <circle cx="38" cy="8" r="3" stroke="currentColor" strokeWidth="0.8" fill="none" />
        <circle cx="38" cy="8" r="1" fill="currentColor" />
      </svg>
      {label && (
        <span className="font-heading italic text-sm tracking-[0.32em] uppercase opacity-90">
          {label}
        </span>
      )}
      <svg width="80" height="16" viewBox="0 0 80 16">
        <circle cx="42" cy="8" r="1" fill="currentColor" />
        <circle cx="42" cy="8" r="3" stroke="currentColor" strokeWidth="0.8" fill="none" />
        <line x1="50" y1="8" x2="80" y2="8" stroke="currentColor" strokeWidth="0.8" />
      </svg>
    </div>
  );
}

/** Lotus divider — used between sections. */
export function LotusDivider({ className = "" }) {
  return (
    <div className={`flex items-center justify-center gap-4 text-[color:var(--brand)]/60 ${className}`} aria-hidden="true">
      <svg width="160" height="20" viewBox="0 0 160 20">
        <line x1="0" y1="10" x2="60" y2="10" stroke="currentColor" strokeWidth="0.6" />
      </svg>
      <svg width="30" height="20" viewBox="0 0 30 30" className="text-[color:var(--brand)]">
        <g stroke="currentColor" strokeWidth="1" fill="none">
          <path d="M15 28 C 6 22, 6 14, 15 4" />
          <path d="M15 28 C 24 22, 24 14, 15 4" />
          <path d="M15 28 C 2 24, 2 16, 15 12" />
          <path d="M15 28 C 28 24, 28 16, 15 12" />
          <circle cx="15" cy="20" r="2" fill="currentColor" />
        </g>
      </svg>
      <svg width="160" height="20" viewBox="0 0 160 20">
        <line x1="100" y1="10" x2="160" y2="10" stroke="currentColor" strokeWidth="0.6" />
      </svg>
    </div>
  );
}

/** Sri-Yantra-inspired stack of nested triangles. Used as a section watermark. */
export function SriYantra({ size = 400, className = "", opacity = 0.06 }) {
  const c = 200;
  const ups = [180, 140, 110, 80];
  const dns = [180, 140, 110, 80, 50];
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 400 400"
      className={className}
      style={{ opacity }}
      aria-hidden="true"
    >
      <circle cx={c} cy={c} r="195" stroke="currentColor" strokeWidth="0.8" fill="none" />
      <circle cx={c} cy={c} r="180" stroke="currentColor" strokeWidth="0.4" fill="none" />
      {/* 8-petal mandala outer */}
      {Array.from({ length: 8 }).map((_, i) => {
        const a = (i * Math.PI) / 4;
        const x1 = c + 180 * Math.cos(a);
        const y1 = c + 180 * Math.sin(a);
        return (
          <path
            key={`p${i}`}
            d={`M${c} ${c} Q ${c + 90 * Math.cos(a + 0.2)} ${c + 90 * Math.sin(a + 0.2)} ${x1} ${y1} Q ${c + 90 * Math.cos(a - 0.2)} ${c + 90 * Math.sin(a - 0.2)} ${c} ${c}`}
            stroke="currentColor"
            strokeWidth="0.6"
            fill="none"
          />
        );
      })}
      {ups.map((h, i) => (
        <polygon
          key={`u${i}`}
          points={`${c},${c - h} ${c - h * 0.866},${c + h / 2} ${c + h * 0.866},${c + h / 2}`}
          stroke="currentColor"
          strokeWidth="0.7"
          fill="none"
        />
      ))}
      {dns.map((h, i) => (
        <polygon
          key={`d${i}`}
          points={`${c},${c + h} ${c - h * 0.866},${c - h / 2} ${c + h * 0.866},${c - h / 2}`}
          stroke="currentColor"
          strokeWidth="0.7"
          fill="none"
        />
      ))}
      <circle cx={c} cy={c} r="4" fill="currentColor" />
    </svg>
  );
}

/** Mandala — concentric petals. Lighter than Sri Yantra. */
export function Mandala({ size = 300, className = "", opacity = 0.08 }) {
  const c = 150;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 300 300"
      className={className}
      style={{ opacity }}
      aria-hidden="true"
    >
      <circle cx={c} cy={c} r="140" stroke="currentColor" strokeWidth="0.6" fill="none" />
      <circle cx={c} cy={c} r="110" stroke="currentColor" strokeWidth="0.4" fill="none" />
      <circle cx={c} cy={c} r="60" stroke="currentColor" strokeWidth="0.4" fill="none" />
      <circle cx={c} cy={c} r="30" stroke="currentColor" strokeWidth="0.6" fill="none" />
      {Array.from({ length: 16 }).map((_, i) => {
        const a = (i * Math.PI) / 8;
        const x = c + 140 * Math.cos(a);
        const y = c + 140 * Math.sin(a);
        const cx1 = c + 80 * Math.cos(a + 0.18);
        const cy1 = c + 80 * Math.sin(a + 0.18);
        const cx2 = c + 80 * Math.cos(a - 0.18);
        const cy2 = c + 80 * Math.sin(a - 0.18);
        return (
          <path
            key={i}
            d={`M${c} ${c} Q ${cx1} ${cy1} ${x} ${y} Q ${cx2} ${cy2} ${c} ${c}`}
            stroke="currentColor"
            strokeWidth="0.5"
            fill="none"
          />
        );
      })}
      <circle cx={c} cy={c} r="3" fill="currentColor" />
    </svg>
  );
}

/** A horizontal repeat of stepped gopuram (temple roof) silhouettes — used as section banding. */
export function GopuramBand({ className = "", height = 28 }) {
  return (
    <svg
      width="100%"
      height={height}
      viewBox="0 0 600 28"
      preserveAspectRatio="none"
      className={className}
      aria-hidden="true"
    >
      <defs>
        <pattern id="gop" x="0" y="0" width="60" height="28" patternUnits="userSpaceOnUse">
          <path
            d="M0 28 L0 22 L8 22 L8 16 L14 16 L14 10 L20 10 L20 6 L26 6 L26 2 L30 0 L34 2 L34 6 L40 6 L40 10 L46 10 L46 16 L52 16 L52 22 L60 22 L60 28 Z"
            fill="currentColor"
          />
        </pattern>
      </defs>
      <rect width="600" height="28" fill="url(#gop)" />
    </svg>
  );
}

/** Manuscript-style ornamental corner. Use 4 rotated copies to frame a card. */
export function ManuscriptCorner({ size = 30, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 30 30" className={className} aria-hidden="true">
      <g stroke="currentColor" strokeWidth="0.8" fill="none">
        <path d="M2 12 L2 2 L12 2" />
        <path d="M6 2 Q 8 6 12 6" />
        <circle cx="4" cy="4" r="1" fill="currentColor" />
      </g>
    </svg>
  );
}

/** Small Bilva-leaf trifoliate (sacred to Shiva). */
export function BilvaLeaf({ size = 28, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" className={className} aria-hidden="true">
      <g stroke="currentColor" strokeWidth="1" fill="none">
        <path d="M16 28 L16 18" />
        <path d="M16 18 C 8 18, 4 12, 6 4 C 12 6, 16 10, 16 18 Z" />
        <path d="M16 18 C 24 18, 28 12, 26 4 C 20 6, 16 10, 16 18 Z" />
        <path d="M16 14 C 13 8, 15 2, 16 0 C 17 2, 19 8, 16 14 Z" />
      </g>
    </svg>
  );
}

/** Diya (oil lamp with flame). */
export function DiyaIcon({ size = 28, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" className={className} aria-hidden="true">
      <g stroke="currentColor" strokeWidth="1" fill="none">
        <path d="M4 22 Q 16 30 28 22 L 24 18 L 8 18 Z" />
        <path d="M16 18 C 15 14, 14 12, 16 8 C 18 12, 17 14, 16 18 Z" fill="currentColor" opacity="0.4" />
      </g>
    </svg>
  );
}

/** Trishul (trident). */
export function Trishul({ size = 28, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" className={className} aria-hidden="true">
      <g stroke="currentColor" strokeWidth="1" fill="none" strokeLinecap="round">
        <line x1="16" y1="6" x2="16" y2="28" />
        <path d="M8 10 L8 4 Q 12 8 16 8 Q 20 8 24 4 L24 10" />
        <line x1="16" y1="8" x2="16" y2="2" />
      </g>
    </svg>
  );
}

/** Kalash (sacred pot with coconut + mango leaves). */
export function Kalash({ size = 32, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" className={className} aria-hidden="true">
      <g stroke="currentColor" strokeWidth="1" fill="none">
        <path d="M8 28 Q 4 18 8 14 L 24 14 Q 28 18 24 28 Z" />
        <line x1="6" y1="14" x2="26" y2="14" />
        <path d="M12 14 Q 16 6 20 14" />
        <circle cx="16" cy="9" r="2" />
      </g>
    </svg>
  );
}

/** Temple-arch image frame. Wraps children inside an arched-top mask. */
export function TempleArchFrame({ children, className = "", ratio = "aspect-[4/5]" }) {
  return (
    <div
      className={`${ratio} overflow-hidden relative ${className}`}
      style={{
        borderRadius: "999px 999px 6px 6px / 22% 22% 6px 6px",
      }}
    >
      {children}
    </div>
  );
}

/** Section watermark — positions a Mandala/Yantra absolutely in a corner. */
export function CornerOrnament({ corner = "tr", element = "mandala", className = "" }) {
  const pos = {
    tl: "top-0 left-0",
    tr: "top-0 right-0",
    bl: "bottom-0 left-0",
    br: "bottom-0 right-0",
  }[corner];
  const translate = {
    tl: "-translate-x-1/3 -translate-y-1/3",
    tr: "translate-x-1/3 -translate-y-1/3",
    bl: "-translate-x-1/3 translate-y-1/3",
    br: "translate-x-1/3 translate-y-1/3",
  }[corner];
  return (
    <div className={`absolute ${pos} ${translate} pointer-events-none text-[color:var(--brand)] ${className}`} aria-hidden="true">
      {element === "yantra" ? <SriYantra size={420} /> : <Mandala size={360} />}
    </div>
  );
}

/** Manuscript drop-cap wrapper. Wraps a paragraph and styles its first letter. */
export function DropCap({ children, className = "" }) {
  return (
    <p className={`drop-cap ${className}`}>{children}</p>
  );
}
